import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useState, type ReactNode, type SyntheticEvent } from "react";
import type { BillingOverview } from "../../shared/billing-schemas";
import type { OpsCustomerSummary, OpsPermission } from "../../shared/operations-schemas";
import { Button } from "../components/ui/button";
import { ContentLoading } from "../components/ContentLoading";
import { Field, FieldError, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Sheet, SheetBody, SheetField, SheetFooter, SheetForm } from "../components/ui/sheet";
import { Textarea } from "../components/ui/textarea";
import { documentMoney } from "../knowledge/customer-document-format";
import { getOrganization, saveCustomerProfile } from "./api";
import { getBilling, billingQueryKey } from "./billing-api";
import { HubCard } from "./CustomerHubs";
import { PageSection } from "./PageSection";
import {
  attentionItems,
  recentActivity,
  type Activity,
  type Attention,
} from "./customer-home-model";
import { useHubOverview } from "./customer-page-queries";
import { agreedLine, dayOf, formText } from "./customer-page-format";
import { CustomerWorkspaceAddress } from "./CustomerWorkspaceAddress";
import { OperationsLink } from "./OperationsLink";
import type { OperationsNavigate } from "./navigation";
import { usePrimaryContact } from "./use-primary-contact";

type HomeProps = {
  customer: OpsCustomerSummary;
  permissions: ReadonlySet<OpsPermission>;
  navigate: OperationsNavigate;
};

function useHomeData({ customer, permissions }: Omit<HomeProps, "navigate">) {
  const ready = customer.provisioningStatus === "ready";
  const canContext = permissions.has("context.read");
  const overview = useHubOverview(customer.id, canContext);
  const billing = useQuery({
    queryKey: billingQueryKey(customer.slug),
    queryFn: () => getBilling(customer.slug),
    enabled: permissions.has("billing.read"),
  });
  const contacts = usePrimaryContact(customer.id, ready && canContext);
  const hubs = overview.data?.hubs ?? [];
  const checked = ready && canContext && !contacts.loading;
  return {
    canContext,
    overview,
    hubs,
    billing: billing.data?.billing,
    contacts,
    attention: attentionItems({
      slug: customer.slug,
      hubs,
      billing: billing.data?.billing,
      needsPrimaryContact: checked && contacts.primary === null,
    }),
  };
}

export function CustomerHome({ customer, permissions, navigate }: HomeProps) {
  const data = useHomeData({ customer, permissions });
  const summary = data.contacts.context.data?.profile?.summary;
  return (
    <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_340px]">
      <div className="min-w-0 space-y-8">
        {summary ? <p className="max-w-3xl text-pretty text-muted-foreground">{summary}</p> : null}
        <NeedsAttention
          items={data.attention}
          navigate={navigate}
          loading={data.canContext && data.overview.isPending}
        />
        {data.canContext ? (
          <HomeTools customerSlug={customer.slug} overview={data.overview} navigate={navigate} />
        ) : null}
        <RecentActivity activity={recentActivity(data.hubs)} />
      </div>
      <AboutCustomer
        customer={customer}
        permissions={permissions}
        billing={data.billing}
        contact={data.contacts.primary}
        profile={data.contacts.context.data?.profile ?? null}
        navigate={navigate}
      />
    </div>
  );
}

function HomeTools({
  customerSlug,
  overview,
  navigate,
}: {
  customerSlug: string;
  overview: ReturnType<typeof useHubOverview>;
  navigate: OperationsNavigate;
}) {
  const hubs = overview.data?.hubs ?? [];
  return (
    <PageSection
      title="Tools"
      action={
        <OperationsLink
          className="font-semibold text-link"
          href={`/customers/${customerSlug}/tools`}
          navigate={navigate}
        >
          All tools
        </OperationsLink>
      }
    >
      {overview.error ? <FieldError>{overview.error.message}</FieldError> : null}
      {overview.data && !hubs.length ? (
        <p className="rounded-xl bg-card p-6 text-muted-foreground shadow-card">
          No tools yet. Add one from the Tools tab when you start scoping it.
        </p>
      ) : null}
      <div className="space-y-4">
        {hubs.map((item) => (
          <HubCard
            key={item.hub.id}
            overview={item}
            customerSlug={customerSlug}
            navigate={navigate}
          />
        ))}
      </div>
    </PageSection>
  );
}

function NeedsAttention({
  items,
  navigate,
  loading,
}: {
  items: readonly Attention[];
  navigate: OperationsNavigate;
  loading: boolean;
}) {
  return (
    <PageSection title="Needs attention">
      {loading ? <ContentLoading label="Checking what needs attention" /> : null}
      {!loading && !items.length ? (
        <p className="rounded-xl bg-card p-6 text-muted-foreground shadow-card">
          Nothing needs attention right now.
        </p>
      ) : null}
      {items.length ? (
        <ul className="divide-y divide-muted rounded-xl bg-card shadow-card">
          {items.map((item) => (
            <li key={item.key}>
              <OperationsLink
                className="flex min-h-16 items-center gap-4 px-6 py-4 no-underline hover:bg-muted/60"
                href={item.href}
                navigate={navigate}
              >
                <span className="size-2.5 shrink-0 rounded-full bg-warning" aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold text-foreground">{item.text}</span>
                  <span className="block text-sm text-muted-foreground">{item.detail}</span>
                </span>
                <ArrowRight className="size-5 shrink-0 text-link" aria-hidden />
              </OperationsLink>
            </li>
          ))}
        </ul>
      ) : null}
    </PageSection>
  );
}

function RecentActivity({ activity }: { activity: readonly Activity[] }) {
  if (!activity.length) return null;
  return (
    <PageSection title="Recent activity">
      <ol className="divide-y divide-muted rounded-xl bg-card shadow-card">
        {activity.map((row) => (
          <li key={row.key} className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-4 px-6 py-3.5">
            <span className="text-sm text-muted-foreground">{dayOf(row.at)}</span>
            <span>
              <span className="font-semibold">{row.kind}</span>
              <span className="text-muted-foreground"> · {row.text}</span>
            </span>
          </li>
        ))}
      </ol>
    </PageSection>
  );
}

function AsideFact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="border-b border-muted py-4 first:pt-0 last:border-b-0 last:pb-0">
      <div className="text-sm font-semibold text-muted-foreground">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

type Contact = ReturnType<typeof usePrimaryContact>["primary"];

function ContactFact({
  contact,
  customerSlug,
  navigate,
}: {
  contact: Contact;
  customerSlug: string;
  navigate: OperationsNavigate;
}) {
  return (
    <AsideFact label="Primary contact">
      {contact ? (
        <>
          <span className="block font-semibold">{contact.displayName}</span>
          <span className="block break-all text-sm text-muted-foreground">
            {contact.email ?? contact.title ?? "No email recorded"}
          </span>
        </>
      ) : (
        <OperationsLink
          className="font-semibold text-link"
          href={`/customers/${customerSlug}/people`}
          navigate={navigate}
        >
          Choose a primary contact
        </OperationsLink>
      )}
    </AsideFact>
  );
}

function WorkspaceFact({ customer, canRead }: { customer: OpsCustomerSummary; canRead: boolean }) {
  const organization = useQuery({
    queryKey: ["ops", "customers", "organization", customer.slug],
    queryFn: () => getOrganization(customer.slug),
    enabled: canRead,
  });
  const account = organization.data?.organization.account;
  return (
    <AsideFact label="Workspace">
      <span className="block font-semibold">{new URL(customer.workspaceUrl).hostname}</span>
      {account ? (
        <span className="block text-sm text-muted-foreground">
          {account.activeUserCount} {account.activeUserCount === 1 ? "person" : "people"} ·{" "}
          {account.samlEnabled ? "Single sign-on" : "Password sign-in"}
        </span>
      ) : null}
    </AsideFact>
  );
}

function AboutCustomer({
  customer,
  permissions,
  billing,
  contact,
  profile,
  navigate,
}: {
  customer: OpsCustomerSummary;
  permissions: ReadonlySet<OpsPermission>;
  billing: BillingOverview | undefined;
  contact: Contact;
  profile: { displayName: string; summary: string | null } | null;
  navigate: OperationsNavigate;
}) {
  const [editing, setEditing] = useState(false);
  const canEdit = permissions.has("context.write") || permissions.has("customer.address.manage");
  return (
    <aside aria-label={`About ${customer.name}`} className="rounded-xl bg-card p-6 shadow-card">
      <ContactFact contact={contact} customerSlug={customer.slug} navigate={navigate} />
      {billing ? (
        <AsideFact label="Billing">
          <span className="block font-semibold">{agreedLine(billing)}</span>
          <span className="block text-sm text-muted-foreground">
            {billing.pastDueCount
              ? `${billing.pastDueCount} past due`
              : `${documentMoney(billing.outstandingMinor)} owed · nothing past due`}
          </span>
        </AsideFact>
      ) : null}
      <WorkspaceFact customer={customer} canRead={permissions.has("organization.read")} />
      {canEdit ? (
        <Button
          variant="secondary"
          className="mt-5 w-full"
          onClick={() => {
            setEditing(true);
          }}
        >
          Edit company details
        </Button>
      ) : null}
      <CompanyDetailsSheet
        open={editing}
        customer={customer}
        profile={profile}
        permissions={permissions}
        onClose={() => {
          setEditing(false);
        }}
      />
    </aside>
  );
}

function CompanyDetailsSheet({
  open,
  customer,
  profile,
  permissions,
  onClose,
}: {
  open: boolean;
  customer: OpsCustomerSummary;
  profile: { displayName: string; summary: string | null } | null;
  permissions: ReadonlySet<OpsPermission>;
  onClose: () => void;
}) {
  const canProfile = permissions.has("context.write") && customer.provisioningStatus === "ready";
  const canAddress = permissions.has("customer.address.manage");
  const [changingAddress, setChangingAddress] = useState(false);
  const addressView = changingAddress || !canProfile;
  const close = () => {
    setChangingAddress(false);
    onClose();
  };
  return (
    <Sheet
      open={open}
      onClose={close}
      title={addressView ? "Workspace address" : "Company details"}
      kicker={customer.name}
    >
      {addressView ? (
        <CustomerWorkspaceAddress
          customer={customer}
          back={
            canProfile ? (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setChangingAddress(false);
                }}
              >
                <ArrowLeft /> Company details
              </Button>
            ) : undefined
          }
        />
      ) : (
        <CompanyProfileForm
          customer={customer}
          profile={profile}
          canAddress={canAddress}
          onChangeAddress={() => {
            setChangingAddress(true);
          }}
          onCancel={close}
          onSaved={close}
        />
      )}
    </Sheet>
  );
}

function CompanyProfileForm({
  customer,
  profile,
  canAddress,
  onChangeAddress,
  onCancel,
  onSaved,
}: {
  customer: OpsCustomerSummary;
  profile: { displayName: string; summary: string | null } | null;
  canAddress: boolean;
  onChangeAddress: () => void;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const client = useQueryClient();
  const save = useMutation({
    mutationFn: (input: { displayName: string; summary: string | null }) =>
      saveCustomerProfile(customer.id, input),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["ops", "context", customer.id] });
      onSaved();
    },
  });
  const submit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    save.mutate({
      displayName: formText(form, "displayName"),
      summary: formText(form, "summary") || null,
    });
  };
  return (
    <SheetForm onSubmit={submit} aria-label="Company profile">
      <SheetBody>
        <Field>
          <FieldLabel htmlFor="company-name">Company name</FieldLabel>
          <Input
            id="company-name"
            name="displayName"
            required
            defaultValue={profile?.displayName ?? customer.name}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="company-summary">What this company does</FieldLabel>
          <Textarea id="company-summary" name="summary" defaultValue={profile?.summary ?? ""} />
        </Field>
        {canAddress ? (
          <SheetField label="Workspace address">
            <span className="mr-3">{new URL(customer.workspaceUrl).hostname}</span>
            <Button
              type="button"
              variant="link"
              className="min-h-0 p-0"
              onClick={() => {
                onChangeAddress();
              }}
            >
              Change address
            </Button>
          </SheetField>
        ) : null}
        {save.error ? <FieldError>{save.error.message}</FieldError> : null}
      </SheetBody>
      <SheetFooter>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? "Saving…" : "Save company details"}
        </Button>
      </SheetFooter>
    </SheetForm>
  );
}
